import { useQuery } from "@tanstack/react-query";
import { createFileRoute, useNavigate, useSearch } from "@tanstack/react-router";
import { type FormEvent, useState } from "react";
import type { CSSProperties } from "react";
import { z } from "zod";
import { useTranslation } from "react-i18next";

import { Spinner } from "../components/ui/Spinner";
import { API_BASE, ApiError, apiFetch } from "../lib/api";
import { queryClient } from "../lib/queryClient";

const searchSchema = z.object({
  next: z.string().optional(),
  error: z.string().optional(),
});

const SSO_ERROR_KEYS: Record<string, string> = {
  sso_failed: "auth.ssoFailed",
  sso_unknown_user: "auth.ssoUnknownUser",
};

export const Route = createFileRoute("/login")({
  staticData: { titleKey: "auth.signIn" },
  validateSearch: searchSchema,
  component: LoginPage,
});

function LoginPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { next, error: ssoErrorCode } = useSearch({ from: "/login" });
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  const { data: ssoStatus } = useQuery({
    queryKey: ["sso-status"],
    queryFn: () => apiFetch<{ enabled: boolean }>("/api/auth/microsoft/status"),
    staleTime: Infinity,
  });
  const ssoErrorKey = ssoErrorCode ? SSO_ERROR_KEYS[ssoErrorCode] : undefined;
  const shownError = error ?? (ssoErrorKey ? t(ssoErrorKey) : null);

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    setSubmitting(true);
    try {
      await apiFetch("/api/auth/login", {
        method: "POST",
        body: JSON.stringify({ email, password }),
      });
      await queryClient.invalidateQueries({ queryKey: ["me"] });
      void navigate({ to: next ?? "/today" });
    } catch (err) {
      if (err instanceof ApiError && err.status === 401) {
        setError(t("auth.invalidCredentials"));
      } else if (
        err instanceof ApiError &&
        err.status === 403 &&
        (err.body as { detail?: string } | null)?.detail === "sso_required"
      ) {
        setError(t("auth.ssoRequired"));
      } else {
        setError(t("auth.signInError"));
      }
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div
      style={{
        minHeight: "100vh",
        display: "grid",
        placeItems: "center",
        padding: 24,
      }}
    >
      <form
        onSubmit={onSubmit}
        style={{
          width: "100%",
          maxWidth: 360,
          background: "var(--bg-elev)",
          border: "1px solid var(--line-soft)",
          borderRadius: "var(--radius-lg)",
          padding: "28px 24px 24px",
          display: "flex",
          flexDirection: "column",
          gap: 16,
        }}
      >
        <div className="eyebrow" style={{ marginBottom: 4 }}>
          {t("auth.brandKicker")}
        </div>
        <h1 className="h-3" style={{ marginBottom: 8 }}>
          {t("auth.signIn")}
        </h1>

        <label style={{ display: "flex", flexDirection: "column", gap: 6 }}>
          <span className="mono" style={{ color: "var(--fg-mute)" }}>
            {t("auth.email")}
          </span>
          {/* `name` + `autoComplete` are what a password manager matches on;
              without them it offered nothing to fill on the one form in the app
              that needs it. Never spellcheck an address. */}
          <input
            type="email"
            name="email"
            autoComplete="username"
            inputMode="email"
            spellCheck={false}
            required
            autoFocus
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            style={inputStyle}
          />
        </label>

        <label style={{ display: "flex", flexDirection: "column", gap: 6 }}>
          <span className="mono" style={{ color: "var(--fg-mute)" }}>
            {t("auth.password")}
          </span>
          <input
            type="password"
            name="password"
            autoComplete="current-password"
            required
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            style={inputStyle}
          />
        </label>

        {/* role="alert" announces on insertion, so the failure is spoken when it
            arrives rather than sitting silently in a corner of the card. */}
        {shownError && (
          <div role="alert" className="mono" style={{ color: "var(--fg)", fontSize: 12 }}>
            {shownError}
          </div>
        )}

        {/* Enabled until the request starts. Disabling it on empty fields also
            blocked Enter — the browser will not implicitly submit a form whose
            default button is disabled — so the form silently did nothing, and
            `required` never got to say which field was missing. */}
        <button
          type="submit"
          className="btn btn--primary"
          disabled={submitting}
          aria-busy={submitting}
          style={{
            marginTop: 4,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            gap: 8,
          }}
        >
          {submitting && <Spinner />}
          {t("auth.signIn")}
        </button>

        {ssoStatus?.enabled && (
          <>
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: 10,
                color: "var(--fg-mute)",
              }}
            >
              <span style={{ flex: 1, borderTop: "1px solid var(--line-soft)" }} />
              <span className="mono" style={{ fontSize: 11 }}>
                {t("auth.or")}
              </span>
              <span style={{ flex: 1, borderTop: "1px solid var(--line-soft)" }} />
            </div>
            <button
              type="button"
              className="btn"
              onClick={() => {
                window.location.assign(`${API_BASE}/api/auth/microsoft/login`);
              }}
              style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 8 }}
            >
              <MicrosoftLogo />
              {t("auth.signInWithMicrosoft")}
            </button>
          </>
        )}
      </form>
    </div>
  );
}

function MicrosoftLogo() {
  return (
    <svg width="14" height="14" viewBox="0 0 21 21" aria-hidden="true">
      <rect x="1" y="1" width="9" height="9" fill="#f25022" />
      <rect x="11" y="1" width="9" height="9" fill="#7fba00" />
      <rect x="1" y="11" width="9" height="9" fill="#00a4ef" />
      <rect x="11" y="11" width="9" height="9" fill="#ffb900" />
    </svg>
  );
}

const inputStyle: CSSProperties = {
  font: "inherit",
  padding: "10px 12px",
  borderRadius: "var(--radius)",
  border: "1px solid var(--line)",
  background: "var(--bg)",
  color: "var(--fg)",
};
